import { createContext, useContext, useState, useEffect, useMemo } from "react"
import { getCookie, setCookie, deleteCookie } from "cookies-next"
import { useGlobalState } from "providers/globalState"
import { isAfter } from "date-fns"
import { useConference } from "services/emma/emma"

const TimezoneContext = createContext({})

function useTimezone() {
    const context = useContext(TimezoneContext)
    if (!context) {
        throw new Error("useTimezone() must be used within a TimezoneProvider")
    }
    return context
}

function TimezoneProvider({ children, defaultZone = "America/New_York" }) {
    const { site } = useGlobalState()
    const savedTimezone = getCookie("timeZone") || site?.meeting?.timezone?.value || defaultZone

    const [timezone, setTimezone] = useState(savedTimezone)
    const { data: conference } = useConference(site?.meeting?.emmaMeetingId, null, !!site?.meeting?.emmaMeetingId)

    // Debug: log cookie value on mount
    useEffect(() => {
        const cookieValue = getCookie("timeZone")
    }, [])

    useEffect(() => {
        if (!conference) return

        // if the meeting has passed, then remove the cookie
        if (isAfter(new Date(), new Date(site.meeting.meetingEndDate))) {
            deleteCookie("timeZone", { path: "/" })
            return
        }

        // if the meeting is in-person use the conference timezone
        if (conference.type === "In-Person") {
            deleteCookie("timeZone", { path: "/" })
            setTimezone(site.meeting.timezone.value)
            return
        }

        // Read saved timezone from cookie if it exists
        const savedTimezone = getCookie("timeZone")
        if (savedTimezone?.includes("/")) {
            setTimezone(savedTimezone)
        }
    }, [conference, site.meeting.meetingEndDate, site.meeting.timezone.value])

    useEffect(() => {
        if (!site?.meeting?.meetingEndDate) return
        if (!isAfter(new Date(), new Date(site.meeting.meetingEndDate))) {
            const expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
            // @ts-ignore - cookies-next type definitions don't match runtime API
            setCookie("timeZone", timezone, {
                expires: expiryDate,
                path: "/",
                sameSite: "lax",
            })
            // Verify it was set
            const verifyValue = getCookie("timeZone")
        }
    }, [timezone])

    // Memoized so every FormattedTimeString below is not re-rendered whenever the parent renders.
    const value = useMemo(() => ({ timezone, setTimezone }), [timezone])

    return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
}

const TimezoneConsumer = TimezoneContext.Consumer

export { TimezoneProvider, TimezoneConsumer, useTimezone }
